import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { type Component, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Drawer from '~/components/Drawer'
import GoBack from '~/components/GoBack'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import TagInput from '~/components/TagInput'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import FormRenderer from '~/layers/FormRenderer'
import { type Path, useNavigate } from '~/router'
import {
  DEFAULT_STEP_ID,
  FIELD_TYPE_LABELS,
  FIELD_TYPES_WITH_OPTIONS,
  type FormFieldType,
  type FormFieldTypeType,
  type FormFieldWidthType,
  type FormStatusType,
  type FormStepType,
  fieldsOfStep,
  formService,
  isAnswerableField,
  normalizeSteps,
  publicFormUrl,
  WIDTH_SPAN
} from '~/services/form.service'
import { resolveError } from '~/utils/error'

/** La paleta agrupada, como en cualquier constructor: por familia, no alfabética. */
const PALETTE: { group: string; types: FormFieldTypeType[] }[] = [
  { group: 'Diseño', types: ['heading', 'paragraph', 'divider', 'spacer'] },
  { group: 'Texto', types: ['short_text', 'long_text', 'email', 'phone', 'number'] },
  { group: 'Fecha', types: ['date'] },
  { group: 'Opciones', types: ['select', 'radio', 'checkbox', 'yes_no'] },
  { group: 'Avanzado', types: ['signature'] }
]

const TYPE_ICONS: Record<FormFieldTypeType, string> = {
  heading: 'material-symbols-light:title',
  paragraph: 'material-symbols-light:notes',
  short_text: 'material-symbols-light:text-fields',
  long_text: 'material-symbols-light:subject',
  email: 'material-symbols-light:alternate-email',
  phone: 'material-symbols-light:call-outline',
  number: 'material-symbols-light:123',
  date: 'material-symbols-light:calendar-month-outline',
  select: 'material-symbols-light:arrow-drop-down-circle-outline',
  radio: 'material-symbols-light:radio-button-checked-outline',
  checkbox: 'material-symbols-light:check-box-outline',
  yes_no: 'material-symbols-light:toggle-on-outline',
  signature: 'material-symbols-light:draw-outline',
  divider: 'material-symbols-light:horizontal-rule',
  spacer: 'material-symbols-light:height'
}

const TYPE_OPTIONS = Object.entries(FIELD_TYPE_LABELS).map(([value, title]) => ({ value, title }))

const STATUS_OPTIONS = [
  { value: 'draft', title: 'Borrador', subtitle: 'Sólo tú lo ves' },
  { value: 'published', title: 'Publicado', subtitle: 'Cualquiera con el enlace puede responder' },
  { value: 'closed', title: 'Cerrado', subtitle: 'Se ve, pero ya no admite respuestas' }
]

const WIDTH_OPTIONS: { value: FormFieldWidthType; title: string; short: string }[] = [
  { value: 'full', title: 'Fila completa', short: '1/1' },
  { value: 'two_thirds', title: 'Dos tercios', short: '2/3' },
  { value: 'half', title: 'Media fila', short: '1/2' },
  { value: 'third', title: 'Un tercio', short: '1/3' }
]

const uid = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`

const defaultLabel = (type: FormFieldTypeType) => {
  if (type === 'heading') return 'Nueva sección'
  if (type === 'paragraph') return 'Texto informativo'
  return FIELD_TYPE_LABELS[type]
}

const makeField = (type: FormFieldTypeType, stepId: string): FormFieldType => ({
  id: uid('f'),
  type,
  label: defaultLabel(type),
  required: false,
  width: 'full',
  stepId,
  options: FIELD_TYPES_WITH_OPTIONS.includes(type) ? ['Opción 1', 'Opción 2'] : undefined
})

/** Lo que se está arrastrando: un tipo nuevo de la paleta o un campo ya puesto. */
type DragPayloadType = { kind: 'new'; type: FormFieldTypeType } | { kind: 'move'; id: string }

/**
 * Zona de soltado entre tarjetas. Vive a nivel de módulo (y no dentro de la
 * página) porque se usa dentro de un `For`: crear componentes dentro de otro
 * componente rompe la disposición de nodos de Solid al reordenar.
 */
const DropZone: Component<{
  index: number
  active: boolean
  dragging: boolean
  onEnter: (index: number) => void
  onLeave: (index: number) => void
  onDrop: (index: number) => void
}> = props => (
  <div
    class={`transition-all rounded-full ${props.dragging ? 'my-1' : 'my-0'} ${
      props.active ? 'h-10 bg-primary-soft border-2 border-dashed border-primary' : props.dragging ? 'h-3 bg-transparent' : 'h-1'
    }`}
    onDragOver={e => {
      e.preventDefault()
      props.onEnter(props.index)
    }}
    onDragLeave={() => props.onLeave(props.index)}
    onDrop={e => {
      e.preventDefault()
      // Sin esto el `drop` sube al lienzo y se procesaría dos veces.
      e.stopPropagation()
      props.onDrop(props.index)
    }}
  >
    <Show when={props.active}>
      <p class="text-xs text-primary text-center leading-10">Soltar aquí</p>
    </Show>
  </div>
)

/** Vista previa del campo dentro del lienzo: se ve, pero no se responde. */
const FieldPreview: Component<{ field: FormFieldType }> = props => {
  const field = () => props.field

  return (
    <Show
      when={isAnswerableField(field())}
      fallback={
        <Show
          when={field().type === 'heading'}
          fallback={
            <Show
              when={field().type === 'divider'}
              fallback={
                <Show when={field().type === 'spacer'} fallback={<p class="text-sm text-foreground-subtle whitespace-pre-line">{field().label}</p>}>
                  <div class="h-6 rounded bg-surface-muted/60 border border-dashed border-border" />
                </Show>
              }
            >
              <hr class="border-border" />
            </Show>
          }
        >
          <h3 class="text-base font-bold text-foreground">{field().label}</h3>
        </Show>
      }
    >
      <div class="pointer-events-none select-none">
        <p class="text-sm font-medium text-foreground mb-1">
          {field().label}
          <Show when={field().required}>
            <span class="text-danger"> *</span>
          </Show>
        </p>
        <Show when={field().description}>
          <p class="text-xs text-foreground-subtle mb-1">{field().description}</p>
        </Show>

        <Show when={field().type === 'long_text'}>
          <div class="h-16 rounded-lg border border-border bg-surface-muted" />
        </Show>

        <Show when={['short_text', 'email', 'phone', 'number', 'date'].includes(field().type)}>
          <div class="h-10 rounded-lg border border-border bg-surface-muted" />
        </Show>

        <Show when={field().type === 'select'}>
          <div class="h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-end px-3">
            <Icon icon="material-symbols-light:keyboard-arrow-down" size={18} class="text-foreground-subtle" />
          </div>
        </Show>

        <Show when={field().type === 'yes_no'}>
          <div class="h-6 w-11 rounded-full bg-surface-muted border border-border" />
        </Show>

        <Show when={field().type === 'signature'}>
          <div class="h-20 rounded-lg border border-dashed border-border bg-surface-muted grid place-items-center text-foreground-subtle">
            <Icon icon="material-symbols-light:draw-outline" size={24} />
          </div>
        </Show>

        <Show when={field().type === 'radio' || field().type === 'checkbox'}>
          <div class="space-y-1.5 pt-1">
            <For each={(field().options ?? []).slice(0, 4)}>
              {option => (
                <div class="flex items-center gap-2">
                  <div class={`h-4 w-4 border border-border bg-surface-muted ${field().type === 'radio' ? 'rounded-full' : 'rounded'}`} />
                  <span class="text-sm text-foreground-subtle">{option}</span>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </Show>
  )
}

/**
 * Constructor de formularios: paleta a la izquierda, lienzo por pasos en el
 * centro y propiedades a la derecha. Se arrastra desde la paleta al lienzo y se
 * reordena arrastrando las tarjetas.
 *
 * La ruta /admin/formularios/nuevo entra en modo alta; cualquier otro id edita.
 */
const FormBuilderPage: Component = () => {
  const params = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const alert = useAlert()

  const isNew = createMemo(() => params.id === 'nuevo')

  /**
   * El título de la pestaña se congela al montar. solid-meta vuelve a leer los
   * hijos de <Title> al desmontar la página, y para entonces los memos y
   * recursos ya están desechados: leerlos ahí revienta.
   */
  const documentTitle = `${params.id === 'nuevo' ? 'Nuevo formulario' : 'Editar formulario'} | Florez Jaramillo`
  const formId = createMemo(() => (isNew() ? null : Number(params.id)))

  const [slug, setSlug] = createSignal('')
  // Guardar y publicar son la misma acción con distinto estado final.
  const [pendingStatus, setPendingStatus] = createSignal<FormStatusType | null>(null)

  /**
   * Los ajustes del formulario viven en el controlador y no en cinco señales
   * sueltas. No hay `<Form>` porque el panel de propiedades se pinta en dos
   * sitios (a un lado en escritorio, en un cajón en móvil) y se guarda desde la
   * barra superior: el controlador funciona igual sin `<form>`.
   */
  const settingsSchema = z.object({
    title: z.string().trim().min(1, 'Ponle un título al formulario'),
    description: z.string().default(''),
    status: z.enum(['draft', 'published', 'closed']).default('draft'),
    submitLabel: z.string().default(''),
    successMessage: z.string().default('')
  })

  const form = useForm({
    schema: settingsSchema,
    initialValues: { title: '', description: '', status: 'draft', submitLabel: '', successMessage: '' },
    onSubmit: values => persist(values),
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const status = () => (form.values().status as FormStatusType) ?? 'draft'

  const [steps, setSteps] = createSignal<FormStepType[]>([{ id: DEFAULT_STEP_ID, title: 'Paso 1' }])
  const [fields, setFields] = createSignal<FormFieldType[]>([])
  const [activeStepId, setActiveStepId] = createSignal(DEFAULT_STEP_ID)
  const [selectedId, setSelectedId] = createSignal<string | null>(null)

  const [drag, setDrag] = createSignal<DragPayloadType | null>(null)
  const [dropIndex, setDropIndex] = createSignal<number | null>(null)
  const [previewOpen, setPreviewOpen] = createSignal(false)

  // En móvil la paleta y las propiedades no caben a los lados: van en cajones.
  const [paletteOpen, setPaletteOpen] = createSignal(false)
  const [propertiesOpen, setPropertiesOpen] = createSignal(false)

  /** Al tocar una tarjeta en pantalla pequeña se abre el panel de propiedades. */
  const selectField = (id: string) => {
    setSelectedId(id)
    if (window.innerWidth < 1280) setPropertiesOpen(true)
  }

  const [loaded] = createResource(formId, async (id: number) => {
    try {
      const loadedForm = await formService.get(id)
      const loadedSteps = normalizeSteps(loadedForm.steps)
      form.reset({
        title: loadedForm.title,
        description: loadedForm.description ?? '',
        status: loadedForm.status,
        submitLabel: loadedForm.settings?.submitLabel ?? '',
        successMessage: loadedForm.settings?.successMessage ?? ''
      })
      setSlug(loadedForm.slug)
      setSteps(loadedSteps)
      setActiveStepId(loadedSteps[0].id)
      setFields(loadedForm.fields ?? [])
      return loadedForm
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
      return null
    }
  })

  const ready = createMemo(() => isNew() || !loaded.loading)

  const isFirstStep = createMemo(() => steps()[0]?.id === activeStepId())

  /** Campos del paso visible, en orden. */
  const stepFields = createMemo(() => fieldsOfStep(fields(), activeStepId(), isFirstStep()))

  const selected = createMemo(() => fields().find(field => field.id === selectedId()) ?? null)

  const answerableCount = createMemo(() => fields().filter(isAnswerableField).length)

  /**
   * Reconstruye el array completo respetando el orden de los pasos, para que
   * guardar y leer devuelvan siempre la misma secuencia.
   */
  const commitStepFields = (nextForStep: FormFieldType[]) => {
    const current = activeStepId()
    const all = fields()
    const rebuilt = steps().flatMap((step, index) => (step.id === current ? nextForStep : fieldsOfStep(all, step.id, index === 0)))
    setFields(rebuilt)
  }

  const addField = (type: FormFieldTypeType, atIndex?: number) => {
    const field = makeField(type, activeStepId())
    const next = [...stepFields()]
    next.splice(atIndex ?? next.length, 0, field)
    commitStepFields(next)
    setSelectedId(field.id)
    setPaletteOpen(false)
  }

  const moveField = (id: string, toIndex: number) => {
    const next = [...stepFields()]
    const from = next.findIndex(field => field.id === id)
    if (from === -1) return
    const [item] = next.splice(from, 1)
    // Al sacar el elemento, todo lo que venía después se corre una posición.
    next.splice(from < toIndex ? toIndex - 1 : toIndex, 0, item)
    commitStepFields(next)
  }

  const removeField = (id: string) => {
    setFields(fields().filter(field => field.id !== id))
    if (selectedId() === id) setSelectedId(null)
  }

  const duplicateField = (id: string) => {
    const next = [...stepFields()]
    const index = next.findIndex(field => field.id === id)
    if (index === -1) return
    const copy = { ...next[index], id: uid('f') }
    next.splice(index + 1, 0, copy)
    commitStepFields(next)
    setSelectedId(copy.id)
  }

  const patchSelected = <K extends keyof FormFieldType>(key: K, value: FormFieldType[K]) => {
    const id = selectedId()
    if (!id) return
    setFields(fields().map(field => (field.id === id ? { ...field, [key]: value } : field)))
  }

  /** Cambiar de tipo da o quita opciones según lo que necesite el nuevo tipo. */
  const changeSelectedType = (type: FormFieldTypeType) => {
    const id = selectedId()
    if (!id) return
    setFields(
      fields().map(field => {
        if (field.id !== id) return field
        const next: FormFieldType = { ...field, type }
        if (FIELD_TYPES_WITH_OPTIONS.includes(type)) {
          if (!next.options?.length) next.options = ['Opción 1', 'Opción 2']
        } else {
          next.options = undefined
        }
        if (!isAnswerableField(next)) next.required = false
        return next
      })
    )
  }

  // ── Pasos ───────────────────────────────────────────────────────────────────

  const addStep = () => {
    const step = { id: uid('step'), title: `Paso ${steps().length + 1}` }
    setSteps([...steps(), step])
    setActiveStepId(step.id)
  }

  const renameStep = (id: string, value: string) => setSteps(steps().map(step => (step.id === id ? { ...step, title: value } : step)))

  const removeStep = async (id: string) => {
    if (steps().length === 1) {
      toast.addToast({ title: 'El formulario necesita al menos un paso', type: 'warning' })
      return
    }

    const count = fieldsOfStep(fields(), id, steps()[0].id === id).length
    if (count > 0) {
      const ok = await alert.confirmDelete({
        title: 'Eliminar paso',
        message: `Se eliminarán también sus ${count} pregunta(s).`
      })
      if (!ok) return
    }

    const wasFirst = steps()[0].id === id
    setFields(fields().filter(field => (field.stepId ? field.stepId !== id : !wasFirst)))
    const next = steps().filter(step => step.id !== id)
    setSteps(next)
    if (activeStepId() === id) setActiveStepId(next[0].id)
  }

  // ── Arrastrar y soltar ──────────────────────────────────────────────────────

  const onDropAt = (index: number) => {
    const payload = drag()
    setDrag(null)
    setDropIndex(null)
    if (!payload) return

    if (payload.kind === 'new') addField(payload.type, index)
    else moveField(payload.id, index)
  }

  // ── Guardar ─────────────────────────────────────────────────────────────────

  /** Guarda de verdad. El controlador ya ha validado los ajustes cuando llega aquí. */
  const persist = async (values: z.infer<typeof settingsSchema>) => {
    const nextStatus = pendingStatus()

    const payload = {
      title: values.title.trim(),
      description: values.description.trim() || undefined,
      status: nextStatus ?? values.status,
      steps: steps(),
      // Se normaliza el paso por si quedó algún campo heredado sin asignar.
      fields: fields().map(field => ({ ...field, stepId: field.stepId ?? steps()[0].id })),
      settings: {
        successMessage: values.successMessage.trim() || undefined,
        submitLabel: values.submitLabel.trim() || undefined
      }
    }

    if (isNew()) {
      const created = await formService.create(payload)
      toast.addToast({ title: 'Formulario creado', type: 'success' })
      navigate(`/admin/formularios/${created.id}` as Path, { replace: true })
    } else {
      const updated = await formService.update(formId()!, payload)
      setSlug(updated.slug)
      toast.addToast({ title: 'Formulario guardado', type: 'success' })
    }

    if (nextStatus) form.setValue('status', nextStatus)
    setPendingStatus(null)
  }

  const save = (nextStatus?: FormStatusType) => {
    // Lo que el esquema no puede ver: las preguntas del lienzo son otra estructura.
    const unlabeled = fields().find(field => !field.label.trim())
    if (unlabeled) {
      setSelectedId(unlabeled.id)
      toast.addToast({ title: 'Hay una pregunta sin texto', type: 'warning' })
      return
    }

    setPendingStatus(nextStatus ?? null)

    if (!form.valid()) {
      // Al abrir el panel se ve el campo que falta con su mensaje.
      if (window.innerWidth < 1280) setPropertiesOpen(true)
      toast.addToast({ title: form.error('title') ?? 'Revisa los ajustes del formulario', type: 'warning' })
    }

    form.submit()
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicFormUrl(slug()))
      toast.addToast({ title: 'Enlace copiado', type: 'success' })
    } catch {
      alert.info('Enlace del formulario', publicFormUrl(slug()))
    }
  }

  /**
   * Los dos paneles se pintan con estas funciones y no con una variable JSX
   * compartida: en escritorio van a los lados y en móvil dentro de un cajón, y
   * reutilizar los mismos nodos en dos sitios rompe la reactividad de Solid.
   */
  const renderPalette = () => (
    <div class="flex-1 overflow-y-auto p-3 space-y-4">
      <For each={PALETTE}>
        {group => (
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-wide text-foreground-subtle mb-2">{group.group}</p>
            <div class="space-y-1.5">
              <For each={group.types}>
                {type => (
                  <button
                    type="button"
                    draggable
                    onDragStart={e => {
                      setDrag({ kind: 'new', type })
                      e.dataTransfer?.setData('text/plain', type)
                      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'copy'
                    }}
                    onDragEnd={() => {
                      setDrag(null)
                      setDropIndex(null)
                    }}
                    onClick={() => addField(type)}
                    class="w-full flex items-center gap-2 rounded-lg border border-border bg-surface px-2.5 py-2 text-left text-sm text-foreground hover:border-primary hover:bg-primary-soft/40 cursor-grab active:cursor-grabbing transition-colors"
                  >
                    <Icon icon={TYPE_ICONS[type]} size={18} class="text-foreground-subtle shrink-0" />
                    <span class="truncate">{FIELD_TYPE_LABELS[type]}</span>
                  </button>
                )}
              </For>
            </div>
          </div>
        )}
      </For>
      <p class="text-xs text-foreground-subtle pt-2">Arrastra un elemento al lienzo o haz clic para añadirlo al final.</p>
    </div>
  )

  const renderProperties = () => (
    <div class="flex-1 overflow-y-auto p-4">
      <Show
        when={selected()}
        fallback={
          <div class="space-y-4">
            <h2 class="font-semibold text-foreground">Ajustes del formulario</h2>
            <Input label="Título" value={(form.values().title as string) ?? ''} onInput={e => form.setValue('title', e.currentTarget.value)} placeholder="Formulario de contacto" />
            <Textarea label="Descripción" rows={3} value={(form.values().description as string) ?? ''} onInput={e => form.setValue('description', e.currentTarget.value)} />
            <Select label="Estado" options={STATUS_OPTIONS} value={status()} onChange={v => form.setValue('status', (v || 'draft') as FormStatusType)} />
            <Input label="Texto del botón" value={(form.values().submitLabel as string) ?? ''} placeholder="Enviar" onInput={e => form.setValue('submitLabel', e.currentTarget.value)} />
            <Textarea
              label="Mensaje de gracias"
              rows={3}
              value={(form.values().successMessage as string) ?? ''}
              placeholder="¡Gracias! Te contactaremos pronto."
              onInput={e => form.setValue('successMessage', e.currentTarget.value)}
            />

            <Show when={!isNew() && slug() && status() !== 'draft'}>
              <div class="pt-2 border-t border-border">
                <p class="text-xs font-semibold uppercase tracking-wide text-foreground-subtle mb-2">Enlace público</p>
                <p class="text-xs font-mono break-all rounded-lg bg-surface-container px-3 py-2 text-foreground-subtle">{publicFormUrl(slug())}</p>
                <Button size="sm" variant="soft" color="default" class="mt-2" onClick={copyLink} prependIcon="material-symbols-light:link">
                  Copiar enlace
                </Button>
              </div>
            </Show>

            <p class="text-xs text-foreground-subtle pt-2">Selecciona una pregunta del lienzo para editarla.</p>
          </div>
        }
      >
        {field => (
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h2 class="font-semibold text-foreground">Pregunta</h2>
              <Button variant="text" color="default" class="p-1.5! h-8 w-8" onClick={() => setSelectedId(null)} tooltip="Cerrar" icon="material-symbols-light:close" iconSize={18} />
            </div>

            <Select label="Tipo" options={TYPE_OPTIONS} value={field().type} onChange={v => changeSelectedType((v || 'short_text') as FormFieldTypeType)} />

            <Input label={isAnswerableField(field()) ? 'Pregunta' : 'Texto'} value={field().label} onInput={e => patchSelected('label', e.currentTarget.value)} />

            <Textarea label="Ayuda (opcional)" rows={2} value={field().description ?? ''} onInput={e => patchSelected('description', e.currentTarget.value)} />

            <Show when={isAnswerableField(field())}>
              <Input label="Texto de ejemplo" value={field().placeholder ?? ''} placeholder="Se muestra dentro del campo vacío" onInput={e => patchSelected('placeholder', e.currentTarget.value)} />
            </Show>

            <Show when={FIELD_TYPES_WITH_OPTIONS.includes(field().type)}>
              <TagInput label="Opciones" tags={field().options ?? []} onTagsChange={tags => patchSelected('options', tags)} placeholder="Escribe una opción y pulsa Enter" />
            </Show>

            <Select
              label="Ancho"
              options={WIDTH_OPTIONS.map(option => ({ value: option.value, title: option.title, subtitle: option.short }))}
              value={field().width ?? 'full'}
              onChange={v => patchSelected('width', (v || 'full') as FormFieldWidthType)}
            />

            <Show when={isAnswerableField(field())}>
              <Switch label="Obligatoria" checked={field().required ?? false} onChange={e => patchSelected('required', e.currentTarget.checked)} />
            </Show>

            <Show when={steps().length > 1}>
              <Select
                label="Paso"
                options={steps().map(step => ({ value: step.id, title: step.title }))}
                value={field().stepId ?? steps()[0].id}
                onChange={v => patchSelected('stepId', String(v || steps()[0].id))}
              />
            </Show>

            <div class="pt-2 border-t border-border flex gap-2">
              <Button size="sm" variant="soft" color="default" onClick={() => duplicateField(field().id)} prependIcon="material-symbols-light:content-copy-outline">
                Duplicar
              </Button>
              <Button size="sm" variant="soft" color="danger" onClick={() => removeField(field().id)} prependIcon="material-symbols-light:delete-outline">
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Show>
    </div>
  )

  return (
    <>
      <Title>{documentTitle}</Title>

      {/* A pantalla completa dentro del área de contenido: el lienzo necesita el espacio. */}
      <div class="-m-4 sm:-m-6 h-[calc(100dvh-4rem)] flex flex-col bg-surface-container">
        {/* Barra del constructor */}
        <header class="h-14 shrink-0 flex items-center justify-between gap-3 px-3 sm:px-4 border-b border-border bg-surface">
          <div class="flex items-center gap-2 min-w-0">
            <GoBack label="Volver a formularios" fallbackPath="/admin/formularios" />
            <div class="min-w-0">
              <p class="font-semibold text-foreground truncate leading-tight">{(form.values().title as string) || 'Formulario sin título'}</p>
              <p class="text-xs text-foreground-subtle truncate">
                {answerableCount()} pregunta(s) · {steps().length} paso(s)
                <Show when={!isNew() && slug()}> · /f/{slug()}</Show>
              </p>
            </div>
          </div>

          {/* En móvil estas acciones viven en la barra inferior, no aquí. */}
          <div class="hidden md:flex items-center gap-2 shrink-0">
            <Button size="sm" variant="soft" color="default" class="xl:hidden" onClick={() => setPropertiesOpen(true)} tooltip="Ajustes" icon="material-symbols-light:tune" />

            <Show when={!isNew() && slug() && status() !== 'draft'}>
              <Button variant="text" color="default" class="hidden sm:inline-flex" size="sm" onClick={copyLink} tooltip="Copiar enlace" icon="material-symbols-light:link" />
              <Button
                variant="text"
                color="default"
                class="hidden sm:inline-flex"
                size="sm"
                onClick={() => window.open(publicFormUrl(slug()), '_blank', 'noopener')}
                tooltip="Abrir formulario"
                icon="material-symbols-light:open-in-new"
              />
            </Show>

            <Button variant="soft" color="info" size="sm" onClick={() => setPreviewOpen(true)} prependIcon="material-symbols-light:visibility-outline" disabled={fields().length === 0}>
              Previsualizar
            </Button>

            <Button variant="soft" color="primary" size="sm" disabled={form.submitting()} onClick={() => save()}>
              {form.submitting() ? 'Guardando...' : 'Guardar'}
            </Button>

            <Show when={status() !== 'published'}>
              <Button size="sm" disabled={form.submitting()} onClick={() => save('published')} prependIcon="material-symbols-light:rocket-launch-outline">
                Publicar
              </Button>
            </Show>
          </div>
        </header>

        <Show when={ready()} fallback={<div class="flex-1 grid place-items-center text-foreground-subtle">Cargando...</div>}>
          <div class="flex-1 flex min-h-0">
            {/* Paleta */}
            <aside class="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-surface">{renderPalette()}</aside>

            {/* Lienzo */}
            <section class="flex-1 min-w-0 flex flex-col">
              {/* Pasos */}
              <div class="shrink-0 flex items-center gap-1 px-3 sm:px-4 border-b border-border bg-surface overflow-x-auto">
                <For each={steps()}>
                  {(step, index) => (
                    <button
                      type="button"
                      onClick={() => setActiveStepId(step.id)}
                      class={`group relative flex items-center gap-2 px-3 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                        activeStepId() === step.id ? 'border-primary text-foreground font-semibold' : 'border-transparent text-foreground-subtle hover:text-foreground'
                      }`}
                    >
                      <span class="text-xs opacity-60">{index() + 1}</span>
                      {step.title}
                      <span class="text-xs rounded-full bg-surface-container px-1.5">{fieldsOfStep(fields(), step.id, index() === 0).length}</span>
                    </button>
                  )}
                </For>
                <Button variant="text" color="default" size="sm" class="ml-1" onClick={addStep} prependIcon="material-symbols-light:add">
                  Paso
                </Button>
              </div>

              {/* Campos del paso */}
              <div class="flex-1 overflow-y-auto p-4 sm:p-6">
                <div class="mx-auto max-w-3xl">
                  <Show when={steps().length > 1}>
                    <div class="mb-4 flex items-center gap-2">
                      <Input
                        class="flex-1"
                        label="Nombre del paso"
                        value={steps().find(step => step.id === activeStepId())?.title ?? ''}
                        onInput={e => renameStep(activeStepId(), e.currentTarget.value)}
                      />
                      <Button
                        variant="soft"
                        color="danger"
                        class="p-2! h-9 w-9 mt-5"
                        onClick={() => removeStep(activeStepId())}
                        tooltip="Eliminar paso"
                        icon="material-symbols-light:delete-outline"
                        iconSize={18}
                      />
                    </div>
                  </Show>

                  <div
                    class="min-h-full"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      // Soltar fuera de cualquier tarjeta añade al final del paso.
                      e.preventDefault()
                      if (dropIndex() === null) onDropAt(stepFields().length)
                    }}
                  >
                    {/* Rejilla de 6 columnas, la misma del formulario final: lo que
                        se ve aquí es exactamente cómo quedará. */}
                    <div class="grid grid-cols-1 sm:grid-cols-6 gap-3">
                      <For each={stepFields()}>
                        {(field, index) => (
                          <div
                            draggable
                            onDragStart={e => {
                              setDrag({ kind: 'move', id: field.id })
                              e.dataTransfer?.setData('text/plain', field.id)
                              if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
                            }}
                            onDragEnd={() => {
                              setDrag(null)
                              setDropIndex(null)
                            }}
                            onDragOver={e => {
                              if (!drag()) return
                              e.preventDefault()
                              // La mitad por la que se pasa decide si entra antes o después.
                              const box = e.currentTarget.getBoundingClientRect()
                              const after = e.clientX - box.left > box.width / 2
                              setDropIndex(after ? index() + 1 : index())
                            }}
                            onDrop={e => {
                              e.preventDefault()
                              e.stopPropagation()
                              onDropAt(dropIndex() ?? index())
                            }}
                            onClick={() => selectField(field.id)}
                            class={`group relative rounded-xl border bg-surface px-4 py-3 cursor-pointer transition-all ${WIDTH_SPAN[field.width ?? 'full']} ${
                              selectedId() === field.id ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
                            } ${drag()?.kind === 'move' && (drag() as { id: string }).id === field.id ? 'opacity-40' : ''}`}
                          >
                            {/* Guía de inserción: marca el lado por el que entrará el elemento. */}
                            <Show when={drag() && dropIndex() === index()}>
                              <span class="absolute -left-1.5 inset-y-2 w-1 rounded-full bg-primary" />
                            </Show>
                            <Show when={drag() && dropIndex() === index() + 1}>
                              <span class="absolute -right-1.5 inset-y-2 w-1 rounded-full bg-primary" />
                            </Show>

                            <div class="flex items-start gap-3">
                              <div class="pt-1 cursor-grab active:cursor-grabbing text-foreground-subtle shrink-0">
                                <Icon icon="material-symbols-light:drag-indicator" size={20} />
                              </div>

                              <div class="flex-1 min-w-0">
                                <FieldPreview field={field} />
                              </div>

                              <div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                <Button
                                  variant="text"
                                  color="default"
                                  class="p-1.5! h-8 w-8"
                                  onClick={e => {
                                    e.stopPropagation()
                                    duplicateField(field.id)
                                  }}
                                  tooltip="Duplicar"
                                  icon="material-symbols-light:content-copy-outline"
                                  iconSize={17}
                                />
                                <Button
                                  variant="text"
                                  color="danger"
                                  class="p-1.5! h-8 w-8"
                                  onClick={e => {
                                    e.stopPropagation()
                                    removeField(field.id)
                                  }}
                                  tooltip="Eliminar"
                                  icon="material-symbols-light:delete-outline"
                                  iconSize={17}
                                />
                              </div>
                            </div>

                            {/* El ancho se cambia aquí mismo: es lo que arma las columnas. */}
                            <div class="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <For each={WIDTH_OPTIONS}>
                                {option => (
                                  <button
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation()
                                      setFields(fields().map(f => (f.id === field.id ? { ...f, width: option.value } : f)))
                                    }}
                                    class={`rounded px-1.5 py-0.5 text-[11px] font-mono border transition-colors ${
                                      (field.width ?? 'full') === option.value ? 'border-primary text-primary bg-primary-soft' : 'border-border text-foreground-subtle hover:border-primary/50'
                                    }`}
                                    aria-label={option.title}
                                  >
                                    {option.short}
                                  </button>
                                )}
                              </For>
                            </div>
                          </div>
                        )}
                      </For>
                    </div>

                    <Show when={stepFields().length > 0}>
                      <DropZone
                        index={stepFields().length}
                        active={dropIndex() === stepFields().length}
                        dragging={!!drag()}
                        onEnter={setDropIndex}
                        onLeave={i => dropIndex() === i && setDropIndex(null)}
                        onDrop={onDropAt}
                      />
                    </Show>

                    <Show when={stepFields().length === 0}>
                      <div
                        class={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${dropIndex() === 0 ? 'border-primary bg-primary-soft/40' : 'border-border'}`}
                        onDragOver={e => {
                          e.preventDefault()
                          setDropIndex(0)
                        }}
                        onDrop={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          onDropAt(0)
                        }}
                      >
                        <Icon icon="material-symbols-light:drag-pan" size={40} class="mx-auto mb-2 text-foreground-subtle" />
                        <p class="text-sm font-medium text-foreground">Arrastra aquí un elemento</p>
                        <p class="text-xs text-foreground-subtle mt-1">O añádelo desde la paleta de elementos.</p>
                      </div>
                    </Show>
                  </div>
                </div>
              </div>
            </section>

            {/* Propiedades */}
            <aside class="hidden xl:flex w-80 shrink-0 flex-col border-l border-border bg-surface">{renderProperties()}</aside>
          </div>
        </Show>

        {/* Barra de acciones para móvil: pegada abajo, donde llega el pulgar. */}
        <div class="md:hidden shrink-0 border-t border-border bg-surface px-2 py-2 flex items-center gap-1.5">
          {/* Con Publicar en pantalla no cabe el rótulo de Añadir: se deja el icono. */}
          <Show
            when={status() === 'published'}
            fallback={<Button size="md" variant="soft" color="default" onClick={() => setPaletteOpen(true)} tooltip="Añadir elemento" icon="material-symbols-light:add" />}
          >
            <Button size="md" variant="soft" color="default" class="grow" onClick={() => setPaletteOpen(true)} prependIcon="material-symbols-light:add">
              Añadir
            </Button>
          </Show>
          <Button size="md" variant="soft" color="default" onClick={() => setPropertiesOpen(true)} tooltip="Ajustes" icon="material-symbols-light:tune" />
          <Button
            size="md"
            variant="soft"
            color="default"
            onClick={() => setPreviewOpen(true)}
            disabled={fields().length === 0}
            tooltip="Previsualizar"
            icon="material-symbols-light:visibility-outline"
          />
          <Button size="md" variant="soft" color="default" disabled={form.submitting()} onClick={() => save()} tooltip="Guardar" icon="material-symbols-light:save-outline" />
          <Show when={status() !== 'published'}>
            <Button size="md" class="grow" disabled={form.submitting()} onClick={() => save('published')} prependIcon="material-symbols-light:rocket-launch-outline">
              Publicar
            </Button>
          </Show>
        </div>
      </div>

      {/* Paleta y propiedades para móvil: el mismo contenido que en escritorio. */}
      <Drawer isOpen={paletteOpen()} onClose={() => setPaletteOpen(false)} position="left" size="sm" title="Añadir elemento">
        <div class="h-full flex flex-col">{renderPalette()}</div>
      </Drawer>

      <Drawer isOpen={propertiesOpen()} onClose={() => setPropertiesOpen(false)} position="right" size="sm" title={selected() ? 'Pregunta' : 'Ajustes'}>
        <div class="h-full flex flex-col">{renderProperties()}</div>
      </Drawer>

      {/* Previsualización: el mismo renderizador de la página pública, con el
          envío desactivado. Se monta sólo al abrir para que empiece en blanco. */}
      <Modal isOpen={previewOpen()} onClose={() => setPreviewOpen(false)} size="lg">
        <Modal.Header>
          <div class="flex items-center justify-between gap-4 w-full">
            <div class="min-w-0">
              <h2 class="text-xl font-bold text-foreground truncate">{(form.values().title as string) || 'Formulario sin título'}</h2>
              <p class="text-sm text-foreground-subtle">Así lo verá quien reciba el enlace</p>
            </div>
            <Button
              variant="text"
              color="default"
              class="p-2! h-9 w-9 shrink-0"
              onClick={() => setPreviewOpen(false)}
              aria-label="Cerrar previsualización"
              icon="material-symbols-light:close"
              iconSize={20}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <Show when={form.values().description}>
            <p class="text-sm text-foreground-subtle mb-5 whitespace-pre-line">{form.values().description as string}</p>
          </Show>
          <Show when={previewOpen()}>
            <FormRenderer fields={fields()} steps={steps()} settings={{ submitLabel: (form.values().submitLabel as string) || undefined }} preview />
          </Show>
        </Modal.Body>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default FormBuilderPage
